import { Img } from '@/components/blocks/MaterialImage';
import { MODEL } from '@/components/blocks/ProductDetails/constants';
import { productAboutSx } from '@/components/blocks/ProductDetails/styles/productAbout';
import { getAllTechnicalSpecs } from '@/components/blocks/ProductDetails/utils/utils';
import {
	NAVIGABLE_SECTION_IDS,
	useScrollToNavigableSection,
} from '@/components/content/ProductDetails/hooks/useScrollToNavigableSection';
import { PaperCard } from '@/components/content/ProductDetails/parts/PaperCards';
import { EXTENDED_CATALOG_PROMO_ICON_IMG } from '@/data/constants/promoIconConstants';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { Ribbon } from '@/data/types/Product';
import { parseHTML } from '@/utils/parseHTMLModern';
import { Box, Divider, Typography } from '@mui/material';
import { FC, useContext } from 'react';
import { CDN_BASE_URL, fulfillmentTypes, PRODUCT } from 'shared/custom/constants/commonConstants';
import { getFulfillmentType } from 'shared/custom/utils/inventoryUtils';

export const ProductAbout: FC<{ isKit?: boolean }> = ({ isKit = false }) => {
	const {
		SKU,
		model,
		AboutProductKit,
		AdditionalInfo,
		Availability: {
			labels: { catalogExtendedLogo },
		},
	} = useLocalization('HDMProductDetail');
	const { product: baseProduct, selection } = useContext(ContentContext) as ReturnType<
		typeof useProductDetails
	>;
	const { scrollToNavigableSection } = useScrollToNavigableSection();

	const product = baseProduct?.type === PRODUCT && selection?.sku ? selection?.sku : baseProduct;
	const productModel =
		product?.attributes?.find((att: any) => att.identifier === MODEL)?.values?.[0]?.value ?? null;
	const technicalSpecs = getAllTechnicalSpecs(product?.attributes);

	const handleGoAdditionalInformation = () => {
		scrollToNavigableSection({
			sectionId: NAVIGABLE_SECTION_IDS.PRODUCT_ADDITIONAL_INFO,
		});
	};

	const fulfillmentTypeValues = {
		DROPSHIP: 'VF',
		SPECIAL_ORDER: 'PE',
		DISTRIBUTION_CENTER: 'DC',
	};

	const fulfillmentType = getFulfillmentType(
		product?.attributes,
		product?.ribbons.map((or: Ribbon) => or.identifier)
	);

	const fulfillmentValue =
		fulfillmentType === fulfillmentTypes.DROPSHIP
			? fulfillmentTypeValues.DROPSHIP
			: fulfillmentType === fulfillmentTypes.SPECIAL_ORDER
			? fulfillmentTypeValues.SPECIAL_ORDER
			: undefined;

	return (
		<PaperCard
			sx={productAboutSx.container}
			id="productHighlightsCard"
			data-specifications={JSON.stringify(technicalSpecs)}
		>
			<Typography variant="headingSegment" sx={productAboutSx.text}>
				{AboutProductKit.t()}
			</Typography>
			<Box sx={productAboutSx.detailsContainer}>
				{productModel ? (
					<Box sx={productAboutSx.detailContainer}>
						<Typography variant="bodySubText" sx={productAboutSx.details}>
							{model.t()}
						</Typography>
						<Typography variant="bodySubText" sx={productAboutSx.details} id="productModel">
							{productModel}
						</Typography>
					</Box>
				) : null}
				<Box sx={productAboutSx.detailContainer}>
					<Typography variant="bodySubText" sx={productAboutSx.details}>
						{SKU.t()}
					</Typography>
					<Typography variant="bodySubText" sx={productAboutSx.details} id="productSku">
						{product?.partNumber}
					</Typography>
				</Box>
				{fulfillmentValue ? (
					<Box sx={productAboutSx.detailContainer}>
						<Img
							width={14}
							height={14}
							src={`${CDN_BASE_URL}/${EXTENDED_CATALOG_PROMO_ICON_IMG.EXTEND_CATALOG}`}
							alt={catalogExtendedLogo.t()}
						/>
						<Typography variant="bodySubText" sx={productAboutSx.details}>
							{fulfillmentValue}
						</Typography>
					</Box>
				) : null}
			</Box>
			<Typography variant="bodyBaseline" sx={productAboutSx.text}>
				{product?.shortDescription && parseHTML(product?.shortDescription)}
			</Typography>
			{!isKit ? (
				<>
					<Divider sx={productAboutSx.divider} />
					<Typography
						variant="bodyBaseline"
						sx={productAboutSx.link}
						id="additionalInfo"
						onClick={() => handleGoAdditionalInformation()}
					>
						{AdditionalInfo.t()}
					</Typography>
				</>
			) : null}
		</PaperCard>
	);
};
